import { notificationsApi } from 'api/notifications/notifications.api';
import { EMPTY_ARRAY } from 'constants/defaults';
import { errorMessageFactory } from 'factories/api';
import { showToast } from 'redux/modules/toasts.actions';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import type { Thunk } from 'types/redux';
import type { DeepReadonly } from 'types/utility';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import type { State } from './notifications.types';
import {
  fetchedNotifications,
  fetchedSingleNotification,
  markNotificationAsRead,
  markNotificationAsSeen,
  markNotificationAsUnseen,
  setTabState,
  TabStates,
  withXhrStatusReducer,
  wrapFetching,
  wrapSaving,
} from './notifications.types';

export const initialState: State = {
  notifications: [],
  activeTab: TabStates.ALL,
  xhrStatus: xhrStatusFactory(),
};

const reducer = reducerWithInitialState<DeepReadonly<State>>(initialState)
  .case(setTabState, (state, payload) => {
    return {
      ...state,
      activeTab: payload,
    };
  })
  .case(fetchedNotifications, (state, payload) => {
    const { notifications } = payload;
    return {
      ...state,
      notifications: notifications || EMPTY_ARRAY,
    };
  })
  .case(fetchedSingleNotification, (state, payload) => {
    if (state.notifications.length > 0) {
      const notifications = state.notifications.filter(n => n.id !== payload.id);
      return {
        ...state,
        notifications: [payload, ...notifications],
      };
    }
    return {
      ...state,
      notifications: [payload],
    };
  })
  .case(markNotificationAsRead, (state, payload) => {
    const { id } = payload;

    const notifications = state.notifications.map(notification => {
      if (notification.id !== id) {
        return notification;
      }

      return {
        ...notification,
        read: true,
        seen: true,
      };
    });

    return {
      ...state,
      notifications,
    };
  })
  .case(markNotificationAsSeen, (state, payload) => {
    const { ids } = payload;

    const notifications = state.notifications.map(notification => {
      const seen = ids.includes(notification.id) ? true : notification.seen;
      return {
        ...notification,
        seen,
      };
    });

    return {
      ...state,
      notifications,
    };
  })
  .case(markNotificationAsUnseen, (state, payload) => {
    const { ids } = payload;

    const notifications = state.notifications.map(notification => {
      return {
        ...notification,
        seen: ids.includes(notification.id) ? false : notification.seen,
        read: ids.includes(notification.id) ? false : notification.seen,
      };
    });

    return {
      ...state,
      notifications,
    };
  });

export function fetchNotifications(): Thunk<Promise<unknown>> {
  return async dispatch => {
    try {
      const notifications = await dispatch(wrapFetching(notificationsApi.getNotifications()));
      const payload = { notifications };
      dispatch(fetchedNotifications(payload));
    } catch (e) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(e) }));
      throw e;
    }
  };
}

export function toggleNotificationIdAsRead(id: number): Thunk<Promise<unknown>> {
  return async dispatch => {
    const payload = { id };
    dispatch(markNotificationAsRead(payload));

    try {
      await dispatch(wrapSaving(notificationsApi.markNotificationIdAsRead(id)));
    } catch (e) {
      dispatch(markNotificationAsUnseen({ ids: [id] }));
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(e) }));
      throw e;
    }
  };
}

export function markNotificationIdsAsSeen(
  notificationIds: Array<number> = []
): Thunk<Promise<void>> {
  return async dispatch => {
    // Nothing to mark as seen...
    if (!notificationIds.length) {
      return Promise.resolve();
    }
    const payload = { ids: notificationIds };

    try {
      dispatch(markNotificationAsSeen(payload));
      await dispatch(wrapSaving(notificationsApi.markNotificationIdsAsSeen(notificationIds)));
    } catch (e) {
      dispatch(markNotificationAsUnseen(payload));
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(e) }));
      throw e;
    }
  };
}

export function fetchNotificationById(id: number): Thunk<Promise<void>> {
  return async dispatch => {
    try {
      const result = await dispatch(wrapFetching(notificationsApi.getNotificationById(id)));
      dispatch(fetchedSingleNotification(result));
    } catch (e) {
      dispatch(showToast({ type: 'danger', message: errorMessageFactory(e) }));
      throw e;
    }
  };
}

export default withXhrStatusReducer(reducer);
